import { Typography, useTheme } from '@mui/material';
import React from 'react';
import { BodySize } from './typography.type';

enum ColorValue {
  primary = 'primary',
  secondary = 'secondary',
  black = 'black',
  white = 'white',
  default = 'default',
}

type ColorValueType = keyof typeof ColorValue;
type TextAlign = 'left' | 'center' | 'right';

type Props = {
  size?: keyof typeof BodySize;
  children?: React.ReactNode;
  align?: TextAlign;
  color?: ColorValueType;
  bold?: boolean;
  styles?: Pick<
    React.CSSProperties,
    'lineHeight' | 'color' | 'fontSize' | 'fontWeight' | 'fontFamily' | 'height'
  >;
  id?: string;
  className?: string;
  trimRowCount?: number;
};

function UiText({
  color,
  align,
  styles,
  size = 'body2',
  bold,
  trimRowCount,
  children,
  ...rest
}: Props) {
  const theme = useTheme();

  function getColor(color?: ColorValueType) {
    if (styles?.color) {
      return styles.color;
    }

    switch (color) {
      case ColorValue.primary:
        return theme.palette.themeColor.main;
      case ColorValue.default:
        return theme.bodyColor;
      case ColorValue.secondary:
        return theme.palette.neutral12.main;
      case ColorValue.black:
        return theme.palette.black.main;
      case ColorValue.white:
        return theme.palette.white.main;
    }
    return;
  }

  function getFontSize() {
    if (styles?.fontSize) {
      return styles.fontSize;
    }

    return;
  }

  let trimLongTextStyle: React.CSSProperties | null = null;
  if (trimRowCount) {
    trimLongTextStyle = {
      paddingBottom: '2px',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: Math.floor(trimRowCount),
      WebkitBoxOrient: 'vertical',
    };
  }

  return (
    <Typography
      component="div"
      className="app-ui-text"
      sx={{
        ...trimLongTextStyle,
        ...styles,
        fontSize: getFontSize(),
        fontWeight: bold ? 600 : 400,
        textAlign: align,
        color: getColor(color),
      }}
      variant={BodySize[size]}
      {...rest}
    >
      {children}
    </Typography>
  );
}

export default UiText;
